import React, { useState, useEffect } from 'react';
import { Plus } from "lucide-react";
import { useNavigate, useLocation } from 'react-router-dom';
import { budgetQuotationService } from "../../budget/services/budgetQuotationService";
import { orderingQuotationService } from "../../ordering/services/orderingQuotationService";
import { showApiError } from "@/lib/toast";

import { LeadExecutionTable } from './lead_ececution_table';
import { LeadExecutionDocumentViewer } from './LeadExecutionDocumentViewer';
import { ActionView, ActionDownload, ActionEdit } from "@/components/ui/LatestTableActions";
import { useRowHighlight } from "@/components/ui/RowHighlight";
import { BASE_URL } from "@/lib/API";
import toast from 'react-hot-toast';

export const BudgetTab = ({ project }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [quotations, setQuotations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);

  // Apply row highlighting hook
  const getHighlightClass = useRowHighlight(quotations, '_id', 5000, 'bg-sky-100/80', ['status']);

  const fetchQuotations = async () => {
    try {
      setIsLoading(true);
      
      // Fetch from old budget service
      let budgetRes;
      try { budgetRes = await budgetQuotationService.getQuotations(); } catch(e) {}
      
      // Fetch from new ordering service (for docType = 'Quotation')
      let orderingRes;
      try { orderingRes = await orderingQuotationService.getQuotations(); } catch(e) {}

      const budgetQuotations = budgetRes?.data || [];
      const orderingQuotations = orderingRes?.data || [];
      const filteredOrderingQuotations = orderingQuotations.filter(q => q.docType === 'Quotation');

      const allQuotations = [...budgetQuotations, ...filteredOrderingQuotations];
      
      // Get all possible IDs for the current project's contact
      const leadIdStr = String(project?.leadId?._id || project?.leadId || project?._id || '');
      const contactCustomId = String(project?.leadId?.customId || project?.leadId?.verveId || '');

      const filtered = allQuotations.filter(q => {
        // Exclude Ordering type — only show Initial and Final quotations in Budget tab
        const qType = String(q.quotationType || '').toLowerCase();
        if (qType === 'ordering') return false;

        // Exclude records in Draft or Not Started status from showing up in the Lead Execution budget tab
        const st = String(q.status || '').toLowerCase().trim();
        if (st === 'draft' || st === 'not started' || st === '') return false;

        // Exclude empty placeholder records created merely when linking ID before actual quotation generation
        const hasValidNo = q.quotationNo && q.quotationNo !== '-' && q.quotationNo !== '—' && q.quotationNo !== '--' && String(q.quotationNo).trim() !== '';
        const hasRowsOrAmount = (q.rows && q.rows.length > 0) || (q.items && q.items.length > 0) || Number(q.grandTotal || q.quotationAmount || 0) > 0;
        if (!hasValidNo && !hasRowsOrAmount) return false;

        const qLeadId = String(q.leadId?._id || q.leadId || '');
        const qClientId = String(q.clientId?._id || q.clientId || '');
        const qContactObjId = String(q.contactObj?._id || q.contactObj || '');
        const qContactRef = String(q.contactRef || '');
        const qCustomId = String(q.customId || '');

        return (
          (leadIdStr && (
            qLeadId === leadIdStr ||
            qClientId === leadIdStr ||
            qContactObjId === leadIdStr ||
            qContactRef === leadIdStr
          )) ||
          (contactCustomId && (
            qContactRef === contactCustomId ||
            qCustomId === contactCustomId
          ))
        );
      });
      
      setQuotations(filtered);
    } catch (err) {
      console.error("Error fetching budget quotations:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (project) {
      fetchQuotations();
    }
  }, [project]);

  const handleNavigateToBudget = () => {
    const leadId = project?.leadId?._id || project?.leadId || project?._id;
    const executionId = project?._id || location.pathname.split('/').pop();
    navigate('/budget', { state: { startPrep: true, openForLead: project?.leadId || project, returnToExecutionId: executionId, highlightLeadId: leadId } });
  };

  const handleView = (record) => {
    const pdfUrl = record.pdfUrl ? `${BASE_URL}${record.pdfUrl}` : null;
    if (pdfUrl) {
      setViewerFile({ url: pdfUrl, name: record.quotationNo || 'Quotation' });
      setViewerOpen(true);
    } else {
      toast.error('No PDF available to view');
    }
  };

  const handleEdit = (record) => {
    const leadId = project?.leadId?._id || project?.leadId || project?._id;
    const executionId = project?._id || location.pathname.split('/').pop();
    navigate('/budget', { state: { startPrep: true, openForLead: project?.leadId || project, editQuotation: record, returnToExecutionId: executionId, highlightLeadId: leadId } });
  };

  const handleDownload = async (record) => {
    const pdfUrl = record.pdfUrl ? `${BASE_URL}${record.pdfUrl}` : null;
    if (!pdfUrl) {
      toast.error('No PDF available to download');
      return;
    }

    try {
      const response = await fetch(pdfUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('blum_token')}`
        }
      });
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = record.quotationNo ? `${record.quotationNo.replace(/\//g, '_')}.pdf` : 'Quotation.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Failed to download file");
    }
  };

  const columns = [
    {
      header: "Quotation No",
      render: (record) => (
        <span className="font-semibold text-[#65768e] text-[11px] uppercase tracking-wider whitespace-nowrap">{record.quotationNo || '—'}</span>
      )
    },
    {
      header: "Type",
      render: (record) => (
        <span className="text-[#65768e] text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">
          {record.quotationType === 'initial' ? 'Initial' : record.quotationType === 'final' ? 'Final' : record.quotationType || 'Unknown'}
        </span>
      )
    },
    {
      header: "Site",
      render: (record) => (
        <span className="text-[#65768e] text-[11px] font-semibold">{record.siteName || record.projectName || '—'}</span>
      )
    },
    {
      header: "Designer",
      render: (record) => (
        <span className="text-[#65768e] text-[11px] font-semibold">{record.referenceDesigner || record.designerName || record.designer || '—'}</span>
      )
    },
    {
      header: "Created Date",
      render: (record) => (
        <span className="text-[#65768e] text-[11px] font-semibold">
          {record.createdAt ? new Date(record.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
        </span>
      )
    },
    {
      header: "Amount",
      render: (record) => (
        <span className="text-[#65768e] text-[11px] font-semibold">
          ₹ {Number(record.grandTotal || record.totalAmount || 0).toLocaleString('en-IN')}
        </span>
      )
    },
    {
      header: "Status",
      render: (record) => (
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md ${
          record.status === 'approved' ? 'bg-green-100 text-green-700' :
          record.status === 'rejected' ? 'bg-red-100 text-red-700' :
          record.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
          'bg-slate-100 text-slate-700'
        }`}>
          {record.status || 'Draft'}
        </span>
      )
    },
    {
      header: "Actions",
      render: (record) => (
        <div className="flex items-center gap-1.5">
          <ActionView onClick={() => handleView(record)} title="View" />
          <ActionEdit onClick={() => handleEdit(record)} title="Edit Budget" />
          <ActionDownload onClick={() => handleDownload(record)} title="Download" />
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col animate-in fade-in duration-500">
      <div className="flex flex-col w-full">
        <div className="p-1 mb-2 flex items-center justify-end gap-3">
          <button
            onClick={handleNavigateToBudget}
            className="flex items-center cursor-pointer gap-2 px-4 py-2 rounded-md text-[11px] font-semibold shadow-sm transition-colors border border-[#2b448f] bg-white text-[#2b448f] hover:bg-slate-50"
          >
            <Plus className="w-3.5 h-3.5" />
            Create Budget
          </button>
        </div>
        
        <LeadExecutionTable 
          columns={columns} 
          data={quotations} 
          isLoading={isLoading} 
          getRowClass={(record) => getHighlightClass(record._id)}
        />
      </div>

      <LeadExecutionDocumentViewer 
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        fileUrl={viewerFile?.url}
        fileName={viewerFile?.name}
      />
    </div>
  );
};

export default BudgetTab;
